import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShipmentFields, emptyShipment, toPayload } from "@/components/ShipmentFields";
import { useApi, useMeta, errMsg } from "@/lib/api";

const pick = (s) => ({
  sender: { ...s.sender, email: s.sender.email || "" }, receiver: { ...s.receiver, email: s.receiver.email || "" },
  origin: s.origin, destination: s.destination, package: { ...s.package }, notes: s.notes || "",
});

export function ShipmentDialog({ open, onOpenChange, shipment, onSaved }) {
  const api = useApi();
  const { data: meta } = useMeta();
  const [form, setForm] = useState(emptyShipment());
  const [extra, setExtra] = useState({ status: "Booked", current_location: "", estimated_delivery: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(shipment ? pick(shipment) : emptyShipment());
      setExtra({ status: "Booked", current_location: "", estimated_delivery: shipment?.estimated_delivery || "" });
    }
  }, [open, shipment]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...toPayload(form), estimated_delivery: extra.estimated_delivery || null };
      if (shipment) await api("put", `/admin/shipments/${shipment.id}`, body);
      else await api("post", "/admin/shipments", { ...body, status: extra.status, current_location: extra.current_location });
      toast.success(shipment ? "Shipment updated" : "Shipment created");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="shipment-dialog">
        <DialogHeader><DialogTitle className="font-display">{shipment ? `Edit ${shipment.tracking_number}` : "New shipment"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-8">
          <div className="grid sm:grid-cols-3 gap-4">
            {!shipment && (
              <>
                <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-500">Initial status</Label>
                  <Select value={extra.status} onValueChange={(v) => setExtra({ ...extra, status: v })}>
                    <SelectTrigger data-testid="shipment-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-500">Current location</Label>
                  <Input placeholder="Defaults to origin" value={extra.current_location} onChange={(e) => setExtra({ ...extra, current_location: e.target.value })} data-testid="shipment-current-location-input" />
                </div>
              </>
            )}
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-500">Estimated delivery</Label>
              <Input type="date" value={extra.estimated_delivery || ""} onChange={(e) => setExtra({ ...extra, estimated_delivery: e.target.value })} data-testid="shipment-eta-input" />
            </div>
          </div>
          <ShipmentFields form={form} setForm={setForm} />
          <Button type="submit" disabled={busy} className="bg-[#FF6B00] hover:bg-[#E05D00]" data-testid="shipment-save-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} {shipment ? "Save changes" : "Create shipment"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
